import { defineMessages } from '@atlassian/jira-intl';

export default defineMessages({
	// Common
	linkDetailsSectionHeadingNonfinal: {
		defaultMessage: 'Link details',
		id: 'applinks.administration.ui.edit.link-details-section-heading-nonfinal',
		description: 'Heading for the section containing application link details on the edit page.',
	},
	applinkStatusLoadingTextNonfinal: {
		defaultMessage: 'Loading application link status...',
		id: 'applinks.administration.ui.edit.applink-status-loading-text-nonfinal',
		description:
			'Message that the application link status is currently being loaded, and will be displayed soon.',
	},
	goToRemoteSiteLinkNonfinal: {
		defaultMessage: 'Go to {displayUrl}',
		id: 'applinks.administration.ui.edit.go-to-remote-site-link-nonfinal',
		description: 'Clickable link which navigates the user to the site at URL {displayUrl}',
	},
	formCancelButtonTextNonfinal: {
		defaultMessage: 'Cancel',
		id: 'applinks.administration.ui.edit.form-cancel-button-text-nonfinal',
		description: 'Label for button which discards the changes made on the edit page.',
	},
	formSaveButtonTextNonfinal: {
		defaultMessage: 'Save',
		id: 'applinks.administration.ui.edit.form-save-button-text-nonfinal',
		description: 'Label for button which saves the changes made on the edit page.',
	},
	editMainTitleNonfinal: {
		defaultMessage: 'Edit application link',
		id: 'applinks.administration.ui.edit.main.title-nonfinal',
		description: 'Title for the main edit application link section.',
	},
	seeAllApplinksButtonNonfinal: {
		defaultMessage: 'See all application links',
		id: 'applinks.administration.ui.edit.see-all-application-links-button-nonfinal',
		description:
			'Label for the button which navigates to the page displaying all application links.',
	},
	applinkNotFoundHeaderNonfinal: {
		defaultMessage: 'Application link not found',
		id: 'applinks.administration.ui.edit.applink-not-found-header-nonfinal',
		description: 'Header for the section displayed when an application link is not found.',
	},
	applinkNotFoundDescriptionNonfinal: {
		defaultMessage:
			'The application link you are trying to edit does not exist or has been deleted.',
		id: 'applinks.administration.ui.edit.applink-not-found-description-nonfinal',
		description: 'Description for the section displayed when an application link is not found.',
	},
	applinkNotEditableHeaderNonfinal: {
		defaultMessage: 'Non-editable system link',
		id: 'applinks.administration.ui.edit.applink-not-editable-header-nonfinal',
		description: 'Header for the section displayed when an application link is not editable.',
	},
	securedByMessageNonfinal: {
		defaultMessage: 'Secured by {authMethod}',
		id: 'applinks.administration.ui.edit.secured-by-message-nonfinal',
		description:
			'Message indicating the authentication method used for the application link. {authMethod} will be either "OAuth 1.0" or "OAuth 2.0".',
	},
	productCardHeaderNonfinal: {
		defaultMessage: 'Linked application',
		id: 'applinks.administration.ui.edit.product-card-header-nonfinal',
		description: 'Header for the product card displaying linked application details.',
	},
	errorFlagTitleNonfinal: {
		defaultMessage: 'Failed to update application link',
		id: 'applinks.administration.ui.edit.error-flag-title-nonfinal',
		description: 'Title for the error flag displayed when an application link update fails.',
	},
	errorFlagDescriptionNonfinal: {
		defaultMessage: 'An error occurred while updating the application link. {error}',
		id: 'applinks.administration.ui.edit.error-flag-description-nonfinal',
		description:
			'Description for the error flag displayed when an application link update fails. {error} represents a sentence describing the error.',
	},

	// OAuth 1.0 Edit
	connectionsTitleNonfinal: {
		defaultMessage: 'Connections',
		id: 'applinks.administration.ui.edit.connections-section-header-nonfinal',
		description:
			'Heading for the section containing OAuth 1.0 connection details on the edit page.',
	},
	arrowDisabledHoverTextNonfinal: {
		defaultMessage: 'Authentication is disabled',
		id: 'applinks.administration.ui.edit.arrow-disabled-alt-text-nonfinal',
		description:
			'Accessible label for the authentication status arrow. Shown when the arrow is greyed out.',
	},
	arrowMatchHoverTextNonfinal: {
		defaultMessage: 'Authentication is enabled',
		id: 'applinks.administration.ui.edit.arrow-match-alt-text-nonfinal',
		description:
			'Accessible label for the authentication status arrow. Shown when the arrow is green (enabled).',
	},
	arrowMismatchHoverTextNonfinal: {
		defaultMessage: 'Authentication is mismatched between local and remote',
		id: 'applinks.administration.ui.edit.arrow-mismatch-alt-text-nonfinal',
		description:
			'Accessible label for the authentication status arrow. Shown when the arrow is red with a cross (error).',
	},
	authTableRowDirectionIncomingNonfinal: {
		defaultMessage: 'Incoming',
		id: 'applinks.administration.ui.edit.direction-incoming-nonfinal',
		description: 'Table row heading for information about incoming authentication.',
	},
	authTableRowDirectionOutgoingNonfinal: {
		defaultMessage: 'Outgoing',
		id: 'applinks.administration.ui.edit.direction-outgoing-nonfinal',
		description: 'Table row heading for information about outgoing authentication.',
	},
	authTableColumnDirectionHeadingNonfinal: {
		defaultMessage: 'Direction',
		id: 'applinks.administration.ui.edit.direction-heading-nonfinal',
		description: 'Table column heading for the direction of authentication.',
	},
	authTableColumnLocalAuthenticationHeadingNonfinal: {
		defaultMessage: 'Local authentication',
		id: 'applinks.administration.ui.edit.local-authentication-heading-nonfinal',
		description: 'Table column heading for local authentication.',
	},
	authTableColumnRemoteAuthenticationHeadingNonfinal: {
		defaultMessage: 'Remote authentication',
		id: 'applinks.administration.ui.edit.remote-authentication-heading-nonfinal',
		description: 'Table column heading for remote authentication.',
	},

	// OAuth 2.0 Edit
	outgoingCredentialsButtonTextNonfinal: {
		defaultMessage: 'Update outgoing link credentials',
		id: 'applinks.administration.ui.edit.outgoing-credentials-button-text-nonfinal',
		description: 'Label for the button which opens the modal to update outgoing link credentials.',
	},
	outgoingCredentialsModalTitleNonfinal: {
		defaultMessage: 'Update outgoing link credentials',
		id: 'applinks.administration.ui.edit.outgoing-credentials-modal-title-nonfinal',
		description: 'Title for the pop-up modal which lets the user update outgoing credentials.',
	},
	outgoingLinkTitleNonfinal: {
		defaultMessage: 'Outgoing link',
		id: 'applinks.administration.ui.edit.outgoing-link.title-nonfinal',
		description: 'Title for the section displaying outgoing link details.',
	},
	outgoingLinkDescriptionNonfinal: {
		defaultMessage:
			'Find these details in the external application and enter them below. You may need to ask an admin of the external application for these details.',
		id: 'applinks.administration.ui.edit.outgoing-link.description-nonfinal',
		description: 'Description for the section displaying incoming link details.',
	},
	incomingLinkTitleNonfinal: {
		defaultMessage: 'Incoming link',
		id: 'applinks.administration.ui.edit.incoming-link.title-nonfinal',
		description: 'Title for the section displaying incoming link details.',
	},
	incomingLinkDescriptionNonfinal: {
		defaultMessage:
			"Copy these details to the external application. If you're not an admin of the application, ask an existing admin to make the updates for you.",
		id: 'applinks.administration.ui.edit.incoming-link.description-nonfinal',
		description: 'Description for the section displaying incoming link details.',
	},
	continueLinkingTitleNonfinal: {
		defaultMessage: 'Continue linking to the external application',
		id: 'applinks.administration.ui.edit.continue-linking.title-nonfinal',
		description:
			'Title for the section prompting the user to continue linking to the external application.',
	},
	continueLinkingDescriptionNonfinal: {
		defaultMessage:
			'In order to complete the link, enter the credentials for the incoming and outgoing links below.',
		id: 'applinks.administration.ui.edit.continue-linking.description-nonfinal',
		description:
			'Description for the section prompting the user to continue linking to the external application.',
	},
	oauth2ConfigLoadFailedTitleNonfinal: {
		defaultMessage: 'Failed to load the OAuth 2.0 configuration',
		id: 'applinks.administration.ui.edit.oauth2-config-load-failed-title-nonfinal',
		description:
			'Title for the error message displayed when OAuth 2.0 configuration fails to load.',
	},
	oauth2ConfigLoadFailedDescriptionNonfinal: {
		defaultMessage:
			'An error occurred while trying to load the OAuth 2.0 configuration. This may be because the target application is inaccessible.',
		id: 'applinks.administration.ui.edit.oauth2-config-load-failed-description-nonfinal',
		description:
			'Description for the error message displayed when OAuth 2.0 configuration fails to load.',
	},
});
